/**
 * IDP Review Portal — router.
 *
 * Mirrors the dars-officer-portal router shape: /login (auth) + / (MainLayout
 * with the Workspace nav group's nested routes). Solution-license guard for
 * `idp` — an unlicensed tenant is bounced to /login with ?solutionLocked=idp,
 * matching every other solution portal (VA/assist/fax/dars).
 *
 * Phase 3.1 wires the Workspace routes to placeholder views; the real
 * Dashboard/Inbox/Completed/Exports land in 3.3 and the In-Review workbench
 * in 3.4.
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore, useEntitlements } from '@adaptcx/shared'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false, layout: 'auth' },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredSolution: 'idp' },
    children: [
      // ── Workspace group ──────────────────────────
      { path: '',          name: 'Dashboard', component: () => import('@/views/DashboardView.vue') },
      { path: 'inbox',     name: 'Inbox',     component: () => import('@/views/InboxView.vue') },
      { path: 'review',    name: 'InReview',  component: () => import('@/views/InReviewView.vue') },
      { path: 'completed', name: 'Completed', component: () => import('@/views/CompletedView.vue') },
      { path: 'exports',   name: 'Exports',   component: () => import('@/views/ExportsView.vue') },

      // ── Governance group (Phase 4) ───────────────
      { path: 'provenance', name: 'Provenance', component: () => import('@/views/ProvenanceView.vue') },
      { path: 'audit',      name: 'Audit',      component: () => import('@/views/AuditView.vue') },
      { path: 'redaction',  name: 'Redaction',  component: () => import('@/views/RedactionStudioView.vue') },

      // ── Intelligence group (Phase 5) ─────────────
      { path: 'ask',             name: 'Ask',            component: () => import('@/views/AskView.vue') },
      { path: 'knowledge-graph', name: 'KnowledgeGraph', component: () => import('@/views/KnowledgeGraphView.vue') },
      { path: 'radar',           name: 'RegulatoryRadar', component: () => import('@/views/RegulatoryRadarView.vue') },
      { path: 'semantic-diff',   name: 'SemanticDiff',   component: () => import('@/views/SemanticDiffView.vue') },

      // ── Author group (Phase 6) ───────────────────
      { path: 'schema-studio',   name: 'SchemaStudio',   component: () => import('@/views/SchemaStudioView.vue') },
      { path: 'active-learning', name: 'ActiveLearning', component: () => import('@/views/ActiveLearningView.vue') },
      { path: 'packs',           name: 'Packs',          component: () => import('@/views/PacksView.vue') },

      // ── Platform group (Phase 9) ─────────────────
      { path: 'architecture',    name: 'Architecture',   component: () => import('@/views/ArchitectureView.vue') },

      // ── Account ──────────────────────────────────
      { path: 'profile',         name: 'ProfileSettings', component: () => import('@/views/profile/ProfileSettingsView.vue') },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  if (to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    await authStore.fetchUser()
    if (!authStore.isAuthenticated) {
      return next({ name: 'Login', query: { redirect: to.fullPath } })
    }
  }
  next()
})

router.beforeEach(async (to, _from, next) => {
  const requiredSolution = to.meta.requiredSolution as string | undefined
  if (!requiredSolution) return next()
  const { isSolutionLicensed, loaded, load } = useEntitlements()
  if (!loaded.value) await load()
  if (isSolutionLicensed(requiredSolution)) return next()
  next({ name: 'Login', query: { solutionLocked: requiredSolution } })
})

export default router
